import { pdfjs } from 'react-pdf';
import { ensurePdfWorker } from '../pdfWorkerSetup';
import { readerCall } from './previewTransport';
import { createDocumentPool } from './documentPool';
import { createPageRenderer } from './pdfPageRenderer';
import { analyzePdf } from '../pageAnalysis';

async function openDocument(documentId, accessToken) {
    const opened = await readerCall('open', { documentId, accessToken });
    const auth = { sessionId: opened.session_id, accessToken };
    let pdfPromise, loadingTask, disposed = false;
    const analyses = new Map();
    const plans = new Map();
    async function getPdf() {
        if (disposed) throw new Error('Preview closed');
        if (!pdfPromise) pdfPromise = (async () => {
            await ensurePdfWorker();
            const bytes = await readerCall('source', auth);
            if (disposed) throw new Error('Preview closed');
            loadingTask = pdfjs.getDocument({ data: new Uint8Array(bytes), isEvalSupported: false, canvasMaxAreaInBytes: 16 * 1024 * 1024 });
            const pdf = await loadingTask.promise;
            if (disposed) throw new Error('Preview closed');
            return pdf;
        })().catch(error => { pdfPromise = null; throw error; });
        return pdfPromise;
    }
    const renderer = createPageRenderer(getPdf);
    return {
        opened, byteLength: opened.byte_length || 0, renderer,
        layout(settings) {
            const key = JSON.stringify(settings);
            if (!plans.has(key)) {
                const promise = readerCall('layout', { ...auth, settings }).catch(error => { plans.delete(key); throw error; });
                plans.set(key, promise);
                if (plans.size > 6) plans.delete(plans.keys().next().value);
            }
            return plans.get(key);
        },
        // Orientation and blank pages; once per opened document, shared by the
        // visible tab and background analysis (see useDocumentAnalysis.js).
        analyze(findBlank = true) {
            if (!analyses.has(findBlank)) analyses.set(findBlank, getPdf()
                .then(pdf => analyzePdf(pdf, { findBlank, isCancelled: () => disposed }))
                .catch(error => { analyses.delete(findBlank); throw error; }));
            return analyses.get(findBlank);
        },
        pause: () => renderer.pause(),
        dispose() {
            if (disposed) return;
            disposed = true; plans.clear(); renderer.dispose();
            loadingTask?.destroy().catch(() => {});
            readerCall('close', auth).catch(() => {});
        },
    };
}

export const previewDocuments = createDocumentPool(openDocument);
